import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";

// Enum options extracted from Ruby enums
export const sourceOptions = [
  { value: "fred", label: "Fred" },
  { value: "world_bank", label: "World Bank" },
];

export const frequencyOptions = [
  { value: "annual", label: "Annual" },
  { value: "biweekly", label: "Biweekly" },
  { value: "daily", label: "Daily" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semiannual", label: "Semiannual" },
  { value: "weekly", label: "Weekly" },
];

// Enum options for edit/new forms (using string values for UI but will be converted to numbers by Zod)
export const sourceEditOptions = [
  { value: "0", label: "Fred" },
  { value: "1", label: "World Bank" },
];

export const frequencyEditOptions = [
  { value: "0", label: "Annual" },
  { value: "1", label: "Biweekly" },
  { value: "2", label: "Daily" },
  { value: "3", label: "Monthly" },
  { value: "4", label: "Quarterly" },
  { value: "5", label: "Semiannual" },
  { value: "6", label: "Weekly" },
];

// Utility function for enum preprocessing in forms
export const preprocessEnumToNumber = (val: any) => {
  if (val === "" || val === null || val === undefined) return undefined;
  const num = Number(val);
  return isNaN(num) ? undefined : num;
};

export const filterFinancialIndicatorsSchema = z.object({
  code_contains: z
    .any()
    .transform(preprocessTextAsyncAutocompleteField)
    .optional(),
  source: z.string().nullable().optional(),
  short_name_contains: z
    .any()
    .transform(preprocessTextAsyncAutocompleteField)
    .optional(),
  name_contains: z
    .any()
    .transform(preprocessTextAsyncAutocompleteField)
    .optional(),
  country_contains: z
    .any()
    .transform(preprocessTextAsyncAutocompleteField)
    .optional(),
  frequency: z.string().nullable().optional(),
  created_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterFinancialIndicatorsSchema = z.infer<
  typeof filterFinancialIndicatorsSchema
>;

export const fields: Field[] = [
  {
    name: "code_contains",
    label: "Code",
    type: "asyncAutocomplete",
    base_url: "instit-quantilia/financial_indicators",
    column_name: "code",
  },
  {
    name: "source",
    label: "Source",
    type: "select",
    useTranslation: false,
    options: sourceOptions,
  },
  {
    name: "short_name_contains",
    label: "Short Name",
    type: "asyncAutocomplete",
    base_url: "instit-quantilia/financial_indicators",
    column_name: "short_name",
  },
  {
    name: "name_contains",
    label: "Name",
    type: "asyncAutocomplete",
    base_url: "instit-quantilia/financial_indicators",
    column_name: "name",
  },
  {
    name: "country_contains",
    label: "Country",
    type: "asyncAutocomplete",
    base_url: "instit-quantilia/financial_indicators",
    column_name: "country",
  },
  {
    name: "frequency",
    label: "Frequency",
    type: "select",
    useTranslation: false,
    options: frequencyOptions,
  },
  {
    name: "created_at_from",
    label: "Created From",
    type: "date",
  },
  {
    name: "created_at_to",
    label: "Created To",
    type: "date",
  },
];

export const client = "qtl - referential";

export function FilterFinancialIndicators() {
  return (
    <FilterTable
      client={client}
      schema={filterFinancialIndicatorsSchema}
      tableName="financial_indicators"
      customBuildPath={() => `${client}/financial_indicators`}
      fields={fields}
    />
  );
}
